import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { BarChart3, Users, Package, TrendingUp, Database, Table, ChevronDown, ChevronRight, MessageCircle, ExternalLink } from 'lucide-react';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';
import { useAuth } from '@/context/AuthContext';
import { RelatorioCardProps } from '@/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LabelList,
  Label as RechartsLabel,
  Line,
  CartesianGrid
} from 'recharts';

const DashboardContainer = styled.div`
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const WelcomeSection = styled.div`
  margin-bottom: 32px;
`;

const WelcomeTitle = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #111827;
  margin: 0 0 8px 0;
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const WelcomeSubtitle = styled.p`
  color: #6b7280;
  font-size: 16px;
  margin: 0;
`;

const FilialInfo = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const FilialDetails = styled.div`
  flex: 1;
`;

const FilialName = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const FilialStatus = styled.div`
  font-size: 14px;
  opacity: 0.9;
`;

const TextMeBotCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TextMeBotIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const TextMeBotContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TextMeBotTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const TextMeBotDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 10px 0;
`;

const TextMeBotLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #128c7e;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
    color: #0f766e;
  }
`;

const WhatsappStatusBadge = styled.span<{ $state: string }>`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) =>
    p.$state === 'open' ? '#065f46' : p.$state === 'connecting' ? '#92400e' : '#991b1b'};
  background: ${(p) =>
    p.$state === 'open' ? '#d1fae5' : p.$state === 'connecting' ? '#fef3c7' : '#fee2e2'};
`;

const WhatsappActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const WhatsappActionButton = styled.button`
  border: 1px solid #128c7e;
  background: #128c7e;
  color: white;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #0f766e;
  }
`;

const WhatsappSecondaryButton = styled(WhatsappActionButton)`
  background: white;
  color: #128c7e;

  &:hover:not(:disabled) {
    background: #ecfdf5;
  }
`;

const WhatsappDangerButton = styled(WhatsappActionButton)`
  border-color: #b91c1c;
  background: #dc2626;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }
`;

const WhatsappQrBox = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const WhatsappQrImage = styled.img`
  width: 240px;
  max-width: 100%;
  height: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  padding: 8px;
`;

const WhatsappPairingCode = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #111827;
`;

const RelatoriosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const RelatorioCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  @media (max-width: 768px) {
    padding: 16px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: #3b82f6;
  }
`;

const RelatorioIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const RelatorioContent = styled.div`
  flex: 1;
`;

const RelatorioTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const RelatorioDescription = styled.p`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const QuickStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
`;


const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalCard = styled.div`
  width: 820px;
  max-width: 95vw;
  background: white;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(0,0,0,.15);
	overflow: hidden;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const ModalBody = styled.div`
	padding: 16px;
	flex: 1;
	overflow: auto;
`;

const Filtros = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
	margin-bottom: 16px;
	position: sticky;
	top: 0;
	background: white;
	z-index: 1;
	padding-top: 4px;
	padding-bottom: 8px;
	border-bottom: 1px solid #e5e7eb;

	@media (max-width: 640px) {
		flex-wrap: wrap;
		align-items: flex-start;
	}
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const GraficoContainer = styled.div`
  width: 100%;
	height: clamp(240px, 40vh, 360px);
`;

const TotalResumo = styled.div`
  margin-top: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TotalTitulo = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const CardsGrid = styled.div`
	margin-top: 12px;
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;
	@media (max-width: 1200px) {
		grid-template-columns: repeat(3, 1fr);
	}
	@media (max-width: 900px) {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: 600px) {
		grid-template-columns: 1fr;
	}
`;

const EstoqueArosSection = styled.div`
  margin-bottom: 32px;
`;

const EstoqueArosSubtitle = styled.p`
  margin: -8px 0 16px 0;
  color: #6b7280;
  font-size: 14px;
`;

const EstoqueArosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const EstoqueAroCard = styled.div<{ $hasError?: boolean }>`
  background: white;
  border: 1px solid ${({ $hasError }) => $hasError ? '#fecaca' : '#e5e7eb'};
  border-radius: 16px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
  }
`;

const EstoqueAroHeader = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%);
  border-bottom: 1px solid #e5e7eb;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const EstoqueAroHeaderInfo = styled.div`
  min-width: 0;
`;

const EstoqueAroFilialNome = styled.h3`
  margin: 0 0 6px 0;
  font-size: 16px;
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EstoqueAroTotal = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
`;

const EstoqueAroTotalNumero = styled.span`
  color: white;
  font-weight: 800;
  font-size: 22px;
  line-height: 1;
`;

const EstoqueAroIcone = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const TopAros = styled.div`
  padding: 14px 16px 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
`;

const TopAroChip = styled.div`
  flex: 1;
  min-width: 84px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  border-radius: 12px;
  padding: 10px;
`;

const TopAroLabel = styled.div`
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const TopAroValor = styled.div`
  margin-top: 4px;
  color: #111827;
  font-size: 16px;
  font-weight: 800;
`;

const EstoqueAroTable = styled.div`
  padding: 12px 16px 16px;
  max-height: 520px;
  overflow-y: auto;
`;

const EstoqueAroLegenda = styled.div`
  display: grid;
  grid-template-columns: 54px 1fr 64px 18px;
  gap: 10px;
  padding: 0 0 8px 0;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
`;

const EstoqueAroLinha = styled.button<{ $expanded?: boolean }>`
  display: grid;
  grid-template-columns: 54px 1fr 64px 18px;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  border-top: 1px solid #f1f5f9;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  background: ${({ $expanded }) => $expanded ? '#f8fafc' : 'transparent'};
  font: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8fafc;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const EstoqueAroNome = styled.div`
  display: inline-flex;
  width: fit-content;
  min-width: 42px;
  justify-content: center;
  padding: 5px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 800;
`;

const EstoqueAroBarra = styled.div`
  height: 10px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;

const EstoqueAroBarraPreenchida = styled.div<{ $percentual: number }>`
  width: ${({ $percentual }) => `${Math.max(4, Math.min(100, $percentual))}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #3b82f6 0%, #10b981 100%);
`;

const EstoqueAroQuantidade = styled.div`
  color: #111827;
  font-size: 14px;
  text-align: right;
  font-weight: 800;
`;

const EstoqueAroExpandIcon = styled.div`
  color: #64748b;
  display: flex;
  justify-content: flex-end;
`;

const EstoqueAroDetalhe = styled.div`
  margin: 0 0 8px 64px;
  padding: 10px 12px;
  border: 1px solid #e0e7ff;
  border-radius: 12px;
  background: #f8fafc;
`;

const EstoqueAroDetalheTitulo = styled.div`
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const EstoqueAroMedidaLinha = styled.button<{ $expanded?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 76px 18px;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 7px 0;
  border-top: 1px solid #e2e8f0;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  background: ${({ $expanded }) => $expanded ? '#eef2ff' : 'transparent'};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:first-of-type {
    border-top: 0;
  }

  &:hover {
    background: #eef2ff;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const EstoqueAroMedidaNome = styled.div`
  color: #111827;
  font-weight: 600;
`;

const EstoqueAroMedidaQuantidade = styled.div`
  color: #111827;
  text-align: right;
  font-weight: 800;
`;

const EstoqueAroProdutosLista = styled.div`
  margin: 0 0 8px 12px;
  padding: 8px 10px;
  border-left: 3px solid #bfdbfe;
  background: white;
  border-radius: 10px;
`;

const EstoqueAroProdutoLinha = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px;
  gap: 12px;
  padding: 7px 0;
  border-top: 1px solid #f1f5f9;
  font-size: 12px;

  &:first-of-type {
    border-top: 0;
  }
`;

const EstoqueAroProdutoNome = styled.div`
  color: #111827;
  line-height: 1.3;
  min-width: 0;
`;

const EstoqueAroProdutoQuantidade = styled.div`
  color: #0f172a;
  text-align: right;
  font-weight: 800;
`;

const EstadoMensagem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
`;

type FilialResumoCardProps = {
	nome: string;
	total: number;
	custoProduto: number;
	margem: number;
	taxa: number;
	quantidadeVeiculos: number;
	markup: number;
};

type GraficoTotalFilialApi = {
  filialId: string;
  filialNome: string;
  total: number;
  custo?: number;
  custoProduto?: number;
  despesas?: number;
  lucroReal?: number;
  margem?: number;
  taxaTipoVenda?: number;
  quantidadeVeiculos?: number;
  markup?: number;
};

type GraficoTotalFilial = {
  filialId: string;
  filialNome: string;
  total: number;
  custoProduto: number;
  margem: number;
  taxa: number;
  quantidadeVeiculos: number;
  markup: number;
};

type EstoqueAroItem = {
  aro: string;
  total: number;
};

type EstoqueAroMedidaItem = {
  medidaId?: string | number | null;
  medida: string;
  total: number;
};

type EstoqueAroProdutoItem = {
  controle?: string | number | null;
  produto: string;
  total: number;
};

type EstoqueAroFilial = {
  filialId: string;
  filialNome: string;
  total: number;
  aros: EstoqueAroItem[];
  erro?: string;
};

const normalizarDadosGrafico = (item: GraficoTotalFilialApi): GraficoTotalFilial => {
  const total = Number(item.total || 0);
  const custoProduto = Number(item.custoProduto ?? item.custo ?? 0);
  const margem = item.margem !== undefined
    ? Number(item.margem)
    : (total - custoProduto);
  const taxa = Number(item.taxaTipoVenda || 0);
  const quantidadeVeiculos = Number(item.quantidadeVeiculos || 0);
  const markup = total > 0 ? (margem / total) * 100 : 0;

  return {
    filialId: item.filialId,
    filialNome: item.filialNome,
    total,
    custoProduto,
    margem,
    taxa,
    quantidadeVeiculos,
    markup
  };
};

const formatarQuantidadeEstoque = (valor: number): string => {
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: Number.isInteger(valor) ? 0 : 2,
    maximumFractionDigits: 2
  });
};

const TireIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
    <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="2" />
    <path d="M12 3V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12 18V21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 12H6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M18 12H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M5.65 5.65L7.75 7.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M16.25 16.25L18.35 18.35" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M18.35 5.65L16.25 7.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M7.75 16.25L5.65 18.35" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const FilialResumoCard: React.FC<FilialResumoCardProps> = ({ nome, total, custoProduto, margem, taxa, quantidadeVeiculos, markup }) => {
	return (
		<div style={{ background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 6px 16px rgba(0,0,0,0.06)', borderRadius: 8, padding: 10, minWidth: 0 }}>
			<div style={{ fontWeight: 600, color: '#111827', marginBottom: 8, fontSize: 13, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nome}</div>
			<div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, fontSize: 12, color: '#111827' }}>
				<div style={{ color: '#6b7280' }}>Total</div>
				<div style={{ fontWeight: 600 }}>{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
				<div style={{ color: '#6b7280' }}>Custo Produto</div>
				<div style={{ fontWeight: 600 }}>{custoProduto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
				<div style={{ color: '#6b7280' }}>Margem</div>
				<div style={{ fontWeight: 600 }}>{margem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
				<div style={{ color: '#6b7280' }}>Taxa do tipo de venda</div>
				<div style={{ fontWeight: 600 }}>{taxa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
				<div style={{ color: '#6b7280' }}>Quantidade de veículos</div>
				<div style={{ fontWeight: 600 }}>{quantidadeVeiculos.toLocaleString('pt-BR')}</div>
				<div style={{ color: '#6b7280' }}>MarkUp</div>
				<div style={{ fontWeight: 600 }}>{`${markup.toFixed(1)}%`}</div>
			</div>
		</div>
	);
};

const EstoqueMedidaDetalheProdutos: React.FC<{ filialId: string; medida: EstoqueAroMedidaItem }> = ({ filialId, medida }) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['pneus-estoque-produtos-por-medida', filialId, medida.medidaId],
    enabled: medida.medidaId !== undefined && medida.medidaId !== null,
    queryFn: async () => apiService.obterPneusEstoqueProdutosPorMedidaFilial(filialId, medida.medidaId as string | number),
    staleTime: 5 * 60 * 1000
  });

  const produtos = useMemo<EstoqueAroProdutoItem[]>(() => {
    const dados = data?.data?.dados;
    if (!Array.isArray(dados)) return [];

    return dados.map((item: any) => ({
      controle: item.controle ?? null,
      produto: String(item.produto || ''),
      total: Number(item.total || 0)
    })).filter((item: EstoqueAroProdutoItem) => item.produto);
  }, [data?.data?.dados]);

  return (
    <EstoqueAroProdutosLista>
      {isLoading || isFetching ? (
        <div style={{ color: '#64748b', fontSize: 12 }}>Carregando pneus...</div>
      ) : isError ? (
        <div style={{ color: '#b91c1c', fontSize: 12 }}>Não foi possível carregar os pneus desta medida.</div>
      ) : produtos.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: 12 }}>Nenhum pneu encontrado para esta medida.</div>
      ) : (
        produtos.map((item) => (
          <EstoqueAroProdutoLinha key={`${filialId}-${medida.medidaId}-${item.controle ?? item.produto}`}>
            <EstoqueAroProdutoNome title={item.produto}>{item.produto}</EstoqueAroProdutoNome>
            <EstoqueAroProdutoQuantidade>{formatarQuantidadeEstoque(item.total)}</EstoqueAroProdutoQuantidade>
          </EstoqueAroProdutoLinha>
        ))
      )}
    </EstoqueAroProdutosLista>
  );
};

const EstoqueAroDetalheMedidas: React.FC<{ filialId: string; aro: string }> = ({ filialId, aro }) => {
  const [medidaExpandida, setMedidaExpandida] = useState<string | number | null>(null);
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['pneus-estoque-medidas-por-aro', filialId, aro],
    queryFn: async () => apiService.obterPneusEstoqueMedidasPorAroFilial(filialId, aro),
    staleTime: 5 * 60 * 1000
  });

  const medidas = useMemo<EstoqueAroMedidaItem[]>(() => {
    const dados = data?.data?.dados;
    if (!Array.isArray(dados)) return [];

    return dados.map((item: any) => ({
      medidaId: item.medidaId ?? null,
      medida: String(item.medida || ''),
      total: Number(item.total || 0)
    })).filter((item: EstoqueAroMedidaItem) => item.medida);
  }, [data?.data?.dados]);

  return (
    <EstoqueAroDetalhe>
      <EstoqueAroDetalheTitulo>Medidas do aro {aro}</EstoqueAroDetalheTitulo>
      {isLoading || isFetching ? (
        <div style={{ color: '#64748b', fontSize: 13 }}>Carregando medidas...</div>
      ) : isError ? (
        <div style={{ color: '#b91c1c', fontSize: 13 }}>Não foi possível carregar as medidas deste aro.</div>
      ) : medidas.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: 13 }}>Nenhuma medida encontrada para este aro.</div>
      ) : (
        medidas.map((item) => {
          const medidaKey = item.medidaId ?? item.medida;
          const expanded = medidaExpandida === medidaKey;

          return (
            <React.Fragment key={`${filialId}-${aro}-${medidaKey}`}>
              <EstoqueAroMedidaLinha
                type="button"
                $expanded={expanded}
                aria-expanded={expanded}
                aria-label={`Ver pneus da medida ${item.medida}`}
                onClick={() => setMedidaExpandida(expanded ? null : medidaKey)}
              >
                <EstoqueAroMedidaNome>{item.medida}</EstoqueAroMedidaNome>
                <EstoqueAroMedidaQuantidade>{formatarQuantidadeEstoque(item.total)}</EstoqueAroMedidaQuantidade>
                <EstoqueAroExpandIcon>
                  {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </EstoqueAroExpandIcon>
              </EstoqueAroMedidaLinha>
              {expanded && (
                <EstoqueMedidaDetalheProdutos filialId={filialId} medida={item} />
              )}
            </React.Fragment>
          );
        })
      )}
    </EstoqueAroDetalhe>
  );
};

const EstoqueAroFilialCard: React.FC<{ filial: EstoqueAroFilial }> = ({ filial }) => {
  const [aroExpandido, setAroExpandido] = useState<string | null>(null);
  const arosOrdenados = [...filial.aros].sort((a, b) => b.total - a.total);
  const topAros = arosOrdenados.slice(0, 3);
  const maiorTotalAro = arosOrdenados[0]?.total || 0;

  return (
    <EstoqueAroCard $hasError={!!filial.erro}>
      <EstoqueAroHeader>
        <EstoqueAroHeaderInfo>
          <EstoqueAroFilialNome title={filial.filialNome}>{filial.filialNome}</EstoqueAroFilialNome>
          <EstoqueAroTotal>
            Total em estoque: <EstoqueAroTotalNumero>{formatarQuantidadeEstoque(filial.total)}</EstoqueAroTotalNumero>
          </EstoqueAroTotal>
        </EstoqueAroHeaderInfo>
        <EstoqueAroIcone>
          <TireIcon size={26} />
        </EstoqueAroIcone>
      </EstoqueAroHeader>
      {filial.erro ? (
        <div style={{ padding: 16, color: '#b91c1c', fontSize: 13 }}>
          Não foi possível consultar esta filial.
        </div>
      ) : filial.aros.length === 0 ? (
        <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
          Nenhum estoque de pneu com aro identificado.
        </div>
      ) : (
        <>
          <TopAros>
            {topAros.map((item) => (
              <TopAroChip key={`${filial.filialId}-top-${item.aro}`}>
                <TopAroLabel>Aro {item.aro}</TopAroLabel>
                <TopAroValor>{formatarQuantidadeEstoque(item.total)}</TopAroValor>
              </TopAroChip>
            ))}
          </TopAros>
          <EstoqueAroTable>
            <EstoqueAroLegenda>
              <span>Aro</span>
              <span>Comparativo</span>
              <span style={{ textAlign: 'right' }}>Total</span>
              <span aria-hidden="true" />
            </EstoqueAroLegenda>
            {arosOrdenados.map((item) => {
              const percentual = maiorTotalAro > 0 ? (item.total / maiorTotalAro) * 100 : 0;
              const expanded = aroExpandido === item.aro;
              return (
                <React.Fragment key={`${filial.filialId}-${item.aro}`}>
                  <EstoqueAroLinha
                    type="button"
                    $expanded={expanded}
                    aria-expanded={expanded}
                    aria-label={`Ver medidas do aro ${item.aro} da filial ${filial.filialNome}`}
                    onClick={() => setAroExpandido(expanded ? null : item.aro)}
                  >
                    <EstoqueAroNome>{item.aro}</EstoqueAroNome>
                    <EstoqueAroBarra title={`${percentual.toFixed(1)}% do maior aro da filial`}>
                      <EstoqueAroBarraPreenchida $percentual={percentual} />
                    </EstoqueAroBarra>
                    <EstoqueAroQuantidade>{formatarQuantidadeEstoque(item.total)}</EstoqueAroQuantidade>
                    <EstoqueAroExpandIcon>
                      {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </EstoqueAroExpandIcon>
                  </EstoqueAroLinha>
                  {expanded && (
                    <EstoqueAroDetalheMedidas filialId={filial.filialId} aro={item.aro} />
                  )}
                </React.Fragment>
              );
            })}
          </EstoqueAroTable>
        </>
      )}
    </EstoqueAroCard>
  );
};

const Dashboard: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const { user } = useAuth();
  const hasProdutosPorFilial = !!user?.is_admin || (user?.perms || []).includes('produtos_por_filial');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showGrafico, setShowGrafico] = useState(false);

  React.useEffect(() => {
    if (searchParams.get('grafico') === 'total-vendas') {
      setShowGrafico(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [tipoGrafico, setTipoGrafico] = useState<'bar' | 'pie'>('pie');
  const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#84cc16","#f472b6","#a855f7","#eab308","#f97316","#22c55e"]; // paleta consistente

  const { data: whatsappProvider } = useQuery({
    queryKey: ['whatsapp-provider'],
    queryFn: async () => apiService.obterWhatsappProvider(),
    enabled: !!user?.is_admin,
    staleTime: 60_000,
  });

  const [whatsappQrBase64, setWhatsappQrBase64] = useState<string | null>(null);
  const [whatsappPairingCode, setWhatsappPairingCode] = useState<string | null>(null);
  const [whatsappConnectLoading, setWhatsappConnectLoading] = useState(false);
  const [whatsappDisconnectLoading, setWhatsappDisconnectLoading] = useState(false);
  const [whatsappConnectError, setWhatsappConnectError] = useState<string | null>(null);

  const supportsInlineQr = !!whatsappProvider?.supportsInlineQr;

  const {
    data: whatsappStatus,
    refetch: refetchWhatsappStatus,
    isFetching: whatsappStatusLoading,
  } = useQuery({
    queryKey: ['whatsapp-provider-status'],
    queryFn: async () => apiService.obterWhatsappProviderStatus(),
    enabled: !!user?.is_admin && supportsInlineQr,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state === 'connecting' || whatsappQrBase64) return 4000;
      return 20000;
    },
  });

  React.useEffect(() => {
    // Só limpa o QR quando conectar de fato e não estivermos gerando outro.
    if (whatsappStatus?.state === 'open' && !whatsappConnectLoading) {
      setWhatsappQrBase64(null);
      setWhatsappPairingCode(null);
      setWhatsappConnectError(null);
    }
  }, [whatsappStatus?.state, whatsappConnectLoading]);

  const handleGerarQrWhatsapp = async () => {
    setWhatsappConnectLoading(true);
    setWhatsappConnectError(null);
    setWhatsappQrBase64(null);
    setWhatsappPairingCode(null);
    try {
      const result = await apiService.conectarWhatsappProvider();
      setWhatsappQrBase64(result.qrBase64);
      setWhatsappPairingCode(result.pairingCode);
      if (!result.qrBase64 && !result.pairingCode) {
        setWhatsappConnectError(
          result.state === 'open'
            ? 'Ainda há uma sessão ativa. Use Desconectar e tente gerar o QR novamente.'
            : 'A Evolution respondeu, mas não retornou QR Code. Tente novamente.',
        );
      }
      await refetchWhatsappStatus();
    } catch (e: any) {
      setWhatsappConnectError(e?.response?.data?.error || e?.message || 'Falha ao gerar QR Code');
    } finally {
      setWhatsappConnectLoading(false);
    }
  };

  const handleDesconectarWhatsapp = async () => {
    setWhatsappDisconnectLoading(true);
    setWhatsappConnectError(null);
    try {
      await apiService.desconectarWhatsappProvider();
      setWhatsappQrBase64(null);
      setWhatsappPairingCode(null);
      await refetchWhatsappStatus();
    } catch (e: any) {
      setWhatsappConnectError(e?.response?.data?.error || e?.message || 'Falha ao desconectar WhatsApp');
    } finally {
      setWhatsappDisconnectLoading(false);
    }
  };

  const whatsappStateLabel = (() => {
    const state = whatsappStatus?.state || 'unknown';
    if (state === 'open') return 'Conectado';
    if (state === 'connecting') return 'Aguardando leitura';
    if (state === 'close') return 'Desconectado';
    return 'Status desconhecido';
  })();

  const whatsappBusy = whatsappConnectLoading || whatsappDisconnectLoading;
  




  const renderValorTopo = (props: any) => {
    const { x, y, width, value } = props;
    if (typeof value !== 'number') return null;
    const text = value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    return (
      <text x={x + width / 2} y={y - 6} fill="#111827" textAnchor="middle" fontSize={12}>{text}</text>
    );
  };

  const renderNomeVertical = (props: any) => {
    const { x, y, width, height, value } = props;
    if (!value || height < 28) return null;
    const cx = x + width / 2;
    const cy = y + height / 2;
    return (
      <text x={cx} y={cy} fill="#ffffff" textAnchor="middle" fontSize={11} transform={`rotate(-90, ${cx}, ${cy})`}>
        {String(value)}
      </text>
    );
  };

  const renderTooltipComposed = (props: any) => {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0]?.payload || {};
    const nome = String(d?.nome || '');
    const total = Number(d?.total || 0);
    const custoProduto = Number(d?.custoProduto || d?.custo || 0);
    const margem = Number(d?.margem || 0);
    const taxa = Number(d?.taxa || 0);
    const quantidadeVeiculos = Number(d?.quantidadeVeiculos || 0);
    const mk = Number(d?.markup || 0);
    return (
      <div style={{ background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 8px 20px rgba(0,0,0,0.08)', borderRadius: 8, padding: 12, minWidth: 240 }}>
        <div style={{ fontWeight: 700, color: '#111827', marginBottom: 8 }}>{nome}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, fontSize: 13, color: '#111827' }}>
          <div style={{ color: '#6b7280' }}>Total</div>
          <div>{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Custo Produto</div>
          <div>{custoProduto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Margem</div>
          <div>{margem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Taxa do tipo de venda</div>
          <div>{taxa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Quantidade de veículos</div>
          <div>{quantidadeVeiculos.toLocaleString('pt-BR')}</div>
          <div style={{ color: '#6b7280' }}>MarkUp</div>
          <div>{`${mk.toFixed(1)}%`}</div>
        </div>
      </div>
    );
  };

  const renderTooltipPie = (props: any) => {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0]?.payload || {};
    const nome = String(d?.filialNome || d?.name || '');
    const total = Number(d?.total || 0);
    const custoProduto = Number(d?.custoProduto || d?.custo || 0);
    const margem = Number(d?.margem || 0);
    const taxa = Number(d?.taxa || 0);
    const quantidadeVeiculos = Number(d?.quantidadeVeiculos || 0);
    const mk = Number(d?.markup || 0);
    return (
      <div style={{ background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 8px 20px rgba(0,0,0,0.08)', borderRadius: 8, padding: 12, minWidth: 240 }}>
        <div style={{ fontWeight: 700, color: '#111827', marginBottom: 8 }}>{nome}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, fontSize: 13, color: '#111827' }}>
          <div style={{ color: '#6b7280' }}>Total</div>
          <div>{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Custo Produto</div>
          <div>{custoProduto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Margem</div>
          <div>{margem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Taxa do tipo de venda</div>
          <div>{taxa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          <div style={{ color: '#6b7280' }}>Quantidade de veículos</div>
          <div>{quantidadeVeiculos.toLocaleString('pt-BR')}</div>
          <div style={{ color: '#6b7280' }}>MarkUp</div>
          <div>{`${mk.toFixed(1)}%`}</div>
        </div>
      </div>
    );
  };

  const queryKey = useMemo(() => (
    ['vendas-total-grafico-todas', dataInicio, dataFim]
  ), [dataInicio, dataFim]);

  const { data, isFetching, refetch } = useQuery({
    queryKey,
    enabled: showGrafico,
    queryFn: async () => apiService.obterVendasTotalGraficoTodas({ dataInicio, dataFim })
  });

  const {
    data: estoqueAroData,
    isLoading: isLoadingEstoqueAro,
    isFetching: isFetchingEstoqueAro,
    isError: isErrorEstoqueAro
  } = useQuery({
    queryKey: ['pneus-estoque-por-aro-todas'],
    enabled: !!filialSelecionada,
    queryFn: async () => apiService.obterPneusEstoquePorAroTodas()
  });

  const relatorios: RelatorioCardProps[] = [
    {
      titulo: 'Relatório de recebimentos por NC',
      descricao: 'Visualize dados de vendas, faturamento e performance por período',
      icone: <TrendingUp size={24} />,
      rota: '/relatorios/vendas',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Heatmap de Vendas (Dia x Hora)',
      descricao: 'Identifique horários nobres de vendas por dia da semana',
      icone: <BarChart3 size={24} />,
      rota: '/relatorios/heatmap-vendas',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Relatório de Comissões por Técnico',
      descricao: 'Resumo de quantidades e comissões por técnico',
      icone: <TrendingUp size={24} />,
      rota: '/relatorios/comissoes',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Relatório Média de Vendas por Vendedor',
      descricao: 'Média de vendas e recebimentos por vendedor no período',
      icone: <TrendingUp size={24} />,
      rota: '/relatorios/media-vendas-vendedor',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Telemarketing x Vendas de Pneus',
      descricao: 'Cotações do telemarketing que viraram venda de pneus nas filiais',
      icone: <TrendingUp size={24} />,
      rota: '/relatorios/telemarketing-pneus-vendas',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Pneus sem itens obrigatórios',
      descricao: 'Vendas de pneus feitas sem os produtos obrigatórios configurados por aro',
      icone: <Package size={24} />,
      rota: '/relatorios/pneus-sem-obrigatorios',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Pneus - Alerta de Estoque',
      descricao: 'Produtos PNEUS com vendas no período e alertas de reposição de estoque',
      icone: <Package size={24} />,
      rota: '/relatorios/pneus-estoque-alerta',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Vendas por NC',
      descricao: 'Consulte uma ou várias NCs e visualize os itens vendidos por grupo no período',
      icone: <Package size={24} />,
      rota: '/relatorios/pneus-por-nc',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Ponto',
      descricao: 'Irregularidades da manhã e esquecimento de batida',
      icone: <Table size={24} />,
      rota: '/relatorios/ponto',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Grupo, Medidas e Produtos',
      descricao: 'Analise estoque, movimentação e performance de produtos',
      icone: <Package size={24} />,
      rota: '/relatorios/produtos',
      filialId: filialSelecionada?.id
    },
    ...(hasProdutosPorFilial ? [{
      titulo: 'Produtos por Filial',
      descricao: 'Consulte produtos e compare disponibilidade entre as filiais',
      icone: <Package size={24} />,
      rota: '/admin/produtos-por-filial',
      filialId: filialSelecionada?.id
    }] : []),
    {
      titulo: 'Relatório de Clientes',
      descricao: 'Dados de clientes, histórico de compras e análise de comportamento',
      icone: <Users size={24} />,
      rota: '/relatorios/clientes',
      filialId: filialSelecionada?.id
    },
    {
      titulo: 'Explorador de Tabelas',
      descricao: 'Visualize e explore as tabelas do banco de dados da filial',
      icone: <Table size={24} />,
      rota: '/tabelas',
      filialId: filialSelecionada?.id
    }
  ];

  const handleRelatorioClick = (relatorio: RelatorioCardProps) => {
    if (!filialSelecionada) {
      alert('Selecione uma filial primeiro');
      return;
    }
    navigate(relatorio.rota);
  };

  const totaisTodas = data?.data?.totais as GraficoTotalFilialApi[] | undefined;
  const dadosGrafico = useMemo(
    () => (Array.isArray(totaisTodas) ? totaisTodas.map(normalizarDadosGrafico) : []),
    [totaisTodas]
  );
  const estoquePorAro = useMemo<EstoqueAroFilial[]>(() => {
    const dados = estoqueAroData?.data?.dados;
    if (!Array.isArray(dados)) return [];

    return dados.map((filial: any) => ({
      filialId: String(filial.filialId || ''),
      filialNome: String(filial.filialNome || filial.filialId || 'Filial'),
      total: Number(filial.total || 0),
      erro: filial.erro ? String(filial.erro) : undefined,
      aros: Array.isArray(filial.aros)
        ? filial.aros.map((item: any) => ({
          aro: String(item.aro || ''),
          total: Number(item.total || 0)
        })).filter((item: EstoqueAroItem) => item.aro)
        : []
    }));
  }, [estoqueAroData?.data?.dados]);
  const { totalGeral, custoProdutoGeral, margemGeral, taxaTipoVendaGeral, quantidadeVeiculosGeral, markupGeral } = useMemo(() => {
    if (dadosGrafico.length === 0) {
      return {
        totalGeral: 0,
        custoProdutoGeral: 0,
        margemGeral: 0,
        taxaTipoVendaGeral: 0,
        quantidadeVeiculosGeral: 0,
        markupGeral: 0
      };
    }

    const total = dadosGrafico.reduce((sum, item) => sum + item.total, 0);
    const custoProduto = dadosGrafico.reduce((sum, item) => sum + item.custoProduto, 0);
    const margem = dadosGrafico.reduce((sum, item) => sum + item.margem, 0);
    const taxa = dadosGrafico.reduce((sum, item) => sum + item.taxa, 0);
    const quantidadeVeiculos = dadosGrafico.reduce((sum, item) => sum + item.quantidadeVeiculos, 0);
    const mk = total > 0 ? (margem / total) * 100 : 0;

    return {
      totalGeral: total,
      custoProdutoGeral: custoProduto,
      margemGeral: margem,
      taxaTipoVendaGeral: taxa,
      quantidadeVeiculosGeral: quantidadeVeiculos,
      markupGeral: mk
    };
  }, [dadosGrafico]);
  // Sempre exibimos totais por filial (todas as filiais)

  if (!filialSelecionada) {
    return null; // O Layout já mostra o estado vazio
  }

  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeTitle>Dashboard de Relatórios</WelcomeTitle>
        <WelcomeSubtitle>
          Acesse relatórios e dados da sua filial selecionada
        </WelcomeSubtitle>
      </WelcomeSection>

      <FilialInfo>
        <Database size={32} />
        <FilialDetails>
          <FilialName>{filialSelecionada.nome}</FilialName>
          <FilialStatus>
            Status: {filialSelecionada.status === 'connected' ? 'Conectado' : 'Desconectado'} • 
            ID: {filialSelecionada.id}
          </FilialStatus>
        </FilialDetails>
      </FilialInfo>

      {user?.is_admin && (
        <TextMeBotCard>
          <TextMeBotIcon>
            <MessageCircle size={24} />
          </TextMeBotIcon>
          <TextMeBotContent>
            <TextMeBotTitle>
              Reconexão WhatsApp ({whatsappProvider?.label || 'TextMeBot'})
              {supportsInlineQr && (
                <WhatsappStatusBadge $state={whatsappStatus?.state || 'unknown'}>
                  {whatsappStatusLoading && !whatsappStatus ? 'Consultando...' : whatsappStateLabel}
                </WhatsappStatusBadge>
              )}
            </TextMeBotTitle>
            <TextMeBotDescription>
              {whatsappProvider?.reconnectHint ||
                'Se o WhatsApp desconectar da API, abra o link abaixo, escaneie o QR Code no aplicativo (Aparelhos conectados → Conectar um aparelho) e aguarde a confirmação de conexão.'}
            </TextMeBotDescription>

            {supportsInlineQr ? (
              <>
                <WhatsappActions>
                  <WhatsappActionButton
                    type="button"
                    onClick={handleGerarQrWhatsapp}
                    disabled={whatsappBusy}
                  >
                    {whatsappConnectLoading
                      ? 'Gerando QR...'
                      : whatsappStatus?.state === 'open'
                        ? 'Trocar número (gerar QR)'
                        : 'Gerar QR Code'}
                  </WhatsappActionButton>
                  {(whatsappStatus?.state === 'open' || whatsappStatus?.state === 'connecting') && (
                    <WhatsappDangerButton
                      type="button"
                      onClick={handleDesconectarWhatsapp}
                      disabled={whatsappBusy}
                    >
                      {whatsappDisconnectLoading ? 'Desconectando...' : 'Desconectar'}
                    </WhatsappDangerButton>
                  )}
                  <WhatsappSecondaryButton
                    type="button"
                    onClick={() => refetchWhatsappStatus()}
                    disabled={whatsappBusy || whatsappStatusLoading}
                  >
                    Atualizar status
                  </WhatsappSecondaryButton>
                </WhatsappActions>

                {(whatsappConnectError || whatsappStatus?.error) && (
                  <TextMeBotDescription style={{ color: '#b91c1c', marginTop: 10 }}>
                    {whatsappConnectError || whatsappStatus?.error}
                  </TextMeBotDescription>
                )}

                {(whatsappQrBase64 || whatsappPairingCode) && (
                  <WhatsappQrBox>
                    {whatsappQrBase64 && (
                      <WhatsappQrImage src={whatsappQrBase64} alt="QR Code WhatsApp Evolution" />
                    )}
                    {whatsappPairingCode && (
                      <>
                        <TextMeBotDescription style={{ margin: 0 }}>
                          Ou use o código de pareamento no WhatsApp:
                        </TextMeBotDescription>
                        <WhatsappPairingCode>{whatsappPairingCode}</WhatsappPairingCode>
                      </>
                    )}
                    <TextMeBotDescription style={{ margin: 0 }}>
                      No celular: Aparelhos conectados → Conectar um aparelho. Escaneie este QR em até ~20 segundos,
                      sem gerar outro QR no meio. Se o WhatsApp disser que falhou, clique em Gerar QR Code novamente.
                    </TextMeBotDescription>
                  </WhatsappQrBox>
                )}

                {whatsappStatus?.state === 'open' && !whatsappQrBase64 && (
                  <TextMeBotDescription style={{ color: '#065f46', marginTop: 10 }}>
                    WhatsApp conectado na Evolution{whatsappStatus.instance ? ` (${whatsappStatus.instance})` : ''}.
                    Para conectar outro número, use Desconectar ou Trocar número (gerar QR).
                  </TextMeBotDescription>
                )}
              </>
            ) : whatsappProvider?.reconnectUrl ? (
              <TextMeBotLink
                href={whatsappProvider.reconnectUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir painel {whatsappProvider.label}
                <ExternalLink size={14} />
              </TextMeBotLink>
            ) : (
              <TextMeBotDescription>
                Configure as variáveis do provider WhatsApp no backend para exibir o link de reconexão.
              </TextMeBotDescription>
            )}
          </TextMeBotContent>
        </TextMeBotCard>
      )}

      <SectionTitle>Relatórios Disponíveis</SectionTitle>
      <RelatoriosGrid>
        {/* Card Gráfico Total de Vendas alinhado com os demais */}
        <RelatorioCard onClick={() => setShowGrafico(true)}>
          <RelatorioIcon>
            <BarChart3 size={24} />
          </RelatorioIcon>
          <RelatorioContent>
            <RelatorioTitle>Gráfico Total de Vendas</RelatorioTitle>
            <RelatorioDescription>Visualize o total por período (filial ou todas)</RelatorioDescription>
          </RelatorioContent>
        </RelatorioCard>
        {relatorios.map((relatorio, index) => (
          <RelatorioCard
            key={index}
            onClick={() => handleRelatorioClick(relatorio)}
          >
            <RelatorioIcon>
              {relatorio.icone}
            </RelatorioIcon>
            <RelatorioContent>
              <RelatorioTitle>{relatorio.titulo}</RelatorioTitle>
              <RelatorioDescription>{relatorio.descricao}</RelatorioDescription>
            </RelatorioContent>
          </RelatorioCard>
        ))}
      </RelatoriosGrid>

      <EstoqueArosSection>
        <SectionTitle>Estoque de Pneus por Aro</SectionTitle>
        <EstoqueArosSubtitle>
          Total atual em estoque agrupado por aro, separado por filial.
        </EstoqueArosSubtitle>
        {isLoadingEstoqueAro || isFetchingEstoqueAro ? (
          <EstadoMensagem>Carregando estoque por aro...</EstadoMensagem>
        ) : isErrorEstoqueAro ? (
          <EstadoMensagem>Não foi possível carregar o estoque de pneus por aro.</EstadoMensagem>
        ) : estoquePorAro.length === 0 ? (
          <EstadoMensagem>Nenhuma filial retornou dados de estoque por aro.</EstadoMensagem>
        ) : (
          <EstoqueArosGrid>
            {estoquePorAro.map((filial) => (
              <EstoqueAroFilialCard key={filial.filialId} filial={filial} />
            ))}
          </EstoqueArosGrid>
        )}
      </EstoqueArosSection>

      <SectionTitle>Estatísticas Rápidas</SectionTitle>
      <QuickStatsGrid>
        <StatCard>
          <StatValue>-</StatValue>
          <StatLabel>Total de Vendas (Mês)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>-</StatValue>
          <StatLabel>Produtos Cadastrados</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>-</StatValue>
          <StatLabel>Clientes Ativos</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>-</StatValue>
          <StatLabel>Última Atualização</StatLabel>
        </StatCard>
      </QuickStatsGrid>

      {showGrafico && (
        <ModalOverlay onClick={() => setShowGrafico(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Gráfico de Total de Vendas</ModalTitle>
              <div style={{ display: 'flex', gap: 8 }}>
                <Botao onClick={() => setShowGrafico(false)}>Fechar</Botao>
              </div>
            </ModalHeader>
            <ModalBody>
              <Filtros>
                <Campo>
                  <Label>Data Início</Label>
                  <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
                </Campo>
                <Campo>
                  <Label>Data Fim</Label>
                  <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
                </Campo>
                <Campo>
                  <Label>Visualização</Label>
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    <Botao onClick={() => setTipoGrafico('bar')} disabled={tipoGrafico === 'bar'}>Barra</Botao>
                    <Botao onClick={() => setTipoGrafico('pie')} disabled={tipoGrafico === 'pie'}>Pizza</Botao>
                  </div>
                </Campo>
                <Botao onClick={() => refetch()} disabled={isFetching}>Aplicar</Botao>
              </Filtros>

              <GraficoContainer>
                {dadosGrafico.length > 0 && (
                  <ResponsiveContainer width="100%" height="100%">
                    {tipoGrafico === 'pie' ? (
                      <PieChart>
                        <Tooltip content={renderTooltipPie} />
                        <Legend />
                        <Pie data={dadosGrafico}
                          dataKey="total"
                          nameKey="filialNome"
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          label={({ name, value }) => `${name}: ${Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                        >
                          {dadosGrafico.map((t, index) => (
                            <Cell key={`cell-${t.filialId ?? index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                          <RechartsLabel
                            position="center"
                            content={(props: any) => {
                              const { viewBox } = props || {};
                              const cx = viewBox?.cx;
                              const cy = viewBox?.cy;
                              if (typeof cx !== 'number' || typeof cy !== 'number') return null;
                              return (
                                <g>
                                  <text x={cx} y={cy - 6} textAnchor="middle" fontSize={14} fill="#6b7280">MarkUp</text>
                                  <text x={cx} y={cy + 14} textAnchor="middle" fontSize={18} fontWeight={700} fill="#111827">
                                    {`${markupGeral.toFixed(1)}%`}
                                  </text>
                                </g>
                              );
                            }}
                          />
                        </Pie>
                      </PieChart>
                    ) : (
                      <ComposedChart
                        data={dadosGrafico.map(t => ({
                          nome: t.filialNome,
                          total: t.total,
                          custoProduto: t.custoProduto,
                          margem: t.margem,
                          taxa: t.taxa,
                          quantidadeVeiculos: t.quantidadeVeiculos,
                          markup: t.markup
                        }))}
                        margin={{ top: 16, right: 24, bottom: 48, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nome" tick={false} axisLine={false} />
                        <YAxis yAxisId="left" tickFormatter={(v) => Number(v).toLocaleString('pt-BR')} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${Number(v).toFixed(0)}%`} />
                        <Tooltip content={renderTooltipComposed} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="custoProduto" name="Custo Produto" stackId="v" fill="#3b82f6" radius={[6,6,0,0]} />
                        <Bar yAxisId="left" dataKey="margem" name="Margem" stackId="v" fill="#10b981" radius={[6,6,0,0]}>
                          <LabelList dataKey="total" content={renderValorTopo} />
                          <LabelList dataKey="nome" content={renderNomeVertical} />
                        </Bar>
                        <Bar yAxisId="left" dataKey="taxa" name="Taxa do tipo de venda" fill="#a855f7" barSize={10} radius={[6,6,0,0]} />
                        <Line yAxisId="right" type="monotone" dataKey="markup" name="MarkUp (%)" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    )}
                  </ResponsiveContainer>
                )}
              </GraficoContainer>

              {dadosGrafico.length > 0 && (
                <TotalResumo>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <TotalTitulo>Resumo do Período (todas as filiais)</TotalTitulo>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      Período: {new Date(dataInicio).toLocaleDateString('pt-BR')} a {new Date(dataFim).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', textAlign: 'right' }}>
                    <div style={{ fontSize: 16, color: '#111827' }}>
                      <b>Total:</b> {totalGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                    <div style={{ fontSize: 16, color: '#111827' }}>
                      <b>Custo Produto:</b> {custoProdutoGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                    <div style={{ fontSize: 16, color: '#111827' }}>
                      <b>Margem:</b> {margemGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                    <div style={{ fontSize: 16, color: '#111827' }}>
                      <b>Taxa do tipo de venda:</b> {taxaTipoVendaGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                    <div style={{ fontSize: 16, color: '#111827' }}>
                      <b>Quantidade de veículos:</b> {quantidadeVeiculosGeral.toLocaleString('pt-BR')}
                    </div>
                    <div style={{ fontSize: 16, color: '#111827' }}>
                      <b>MarkUp:</b> {`${markupGeral.toFixed(1)}%`}
                    </div>
                  </div>
                </TotalResumo>
              )}

								{dadosGrafico.length > 0 && (
									<>
										<div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>Detalhes por filial</div>
										<CardsGrid>
											{dadosGrafico.map((t, index) => {
												return (
													<FilialResumoCard
														key={t.filialId ?? index}
														nome={t.filialNome}
														total={t.total}
														custoProduto={t.custoProduto}
														margem={t.margem}
														taxa={t.taxa}
														quantidadeVeiculos={t.quantidadeVeiculos}
														markup={t.markup}
													/>
												);
											})}
										</CardsGrid>
									</>
								)}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;